import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Container, Section } from '@vnetwork/ui/layout';
import { TooltipProvider } from '@vnetwork/ui/primitives';
import { useMyServices, serviceProviderScope } from '@/api/services';
import { brandCategoryByKey, categoryOfScope } from './categories';
import { isWaapCapable } from './waap/waap-service';
import { isWafCapable } from './waf/waf-service';
import { groupCDNServices } from './cdn-service-grouping';
import { ProvisionedCard, ProductCard } from './ServicesDashboard';
import { brand } from '@/config/brand';
import { LANDING_CONTENT } from './landing-content';
import grid from './Services.module.css';
import styles from './CategoryLanding.module.css';

/**
 * Generic category landing — product-marketing hero (value props + optional
 * graphic) above the provisioned/available services for a given category.
 *
 * Replaces the hand-rolled ComputeLanding and SecurityLanding components;
 * content is driven by LANDING_CONTENT[categoryKey].
 *
 * Security special-case: WAAP/WAF provisioned services use their dedicated
 * ProvisionedCard variants (asWaap / asWaf) exactly as SecurityLanding did.
 */
export default function CategoryLanding({ categoryKey }: { categoryKey: string }) {
  const { data: services = [] } = useMyServices();
  const category = brandCategoryByKey(categoryKey);
  const content = LANDING_CONTENT[categoryKey];

  // ── Security: WAAP + WAF get their own card variants ───────────────────────
  const isSecurity = categoryKey === 'security';
  const waapServices = useMemo(() => (isSecurity ? services.filter(isWaapCapable) : []), [isSecurity, services]);
  const wafServices = useMemo(() => (isSecurity ? services.filter(isWafCapable) : []), [isSecurity, services]);

  // ── CDN: multiple provider services collapse into one umbrella card ─────────
  const isCdn = categoryKey === 'cdn';
  const cdnGroups = useMemo(() => (isCdn ? groupCDNServices(services) : []), [isCdn, services]);
  const cdnGroupByCanonicalId = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of cdnGroups) m.set(g.canonical.id, g.providerCount);
    return m;
  }, [cdnGroups]);

  // ── Generic: filter provisioned by category scope ──────────────────────────
  const categoryServices = useMemo(() => {
    if (isSecurity) return [];
    if (isCdn) return cdnGroups.map((g) => g.canonical);
    return services.filter((s) => categoryOfScope(serviceProviderScope(s)) === categoryKey);
  }, [isSecurity, isCdn, cdnGroups, services, categoryKey]);

  // Catalog: hide products already provisioned
  const provisionedScopes = useMemo(
    () => new Set(categoryServices.map((s) => serviceProviderScope(s))),
    [categoryServices],
  );
  const catalog = useMemo(() => {
    const products = category?.products ?? [];
    if (isSecurity) {
      return products
        .filter((p) => !(wafServices.length > 0 && p.scope === 'Security.WAF'))
        .filter((p) => !(waapServices.length > 0 && p.scope === 'Security.WAAP'));
    }
    // CDN: once any orchestrator exists, the umbrella card covers the catalog owl.
    if (isCdn && cdnGroups.length > 0) {
      return products.filter((p) => p.scope !== 'External.Mlytics');
    }
    return products.filter((p) => !provisionedScopes.has(p.scope));
  }, [category, isSecurity, isCdn, cdnGroups.length, wafServices.length, waapServices.length, provisionedScopes]);

  // ── Stat strip ─────────────────────────────────────────────────────────────
  const registered = isSecurity
    ? waapServices.length + wafServices.length
    : categoryServices.length;
  const products = category?.products ?? [];
  const available = products.filter((p) => p.status === 'active' || p.status === 'beta').length;
  const comingSoon = products.filter((p) => p.status === 'coming_soon').length;

  if (!content) return null;

  const hasArt = !!content.heroArt;
  const hasDarkArt = !!content.heroArtDark;

  return (
    <TooltipProvider>
      <Container size="xl">
        <div className={styles.banner}>
          <span className={styles.bannerGrid} aria-hidden />
          <div className={styles.hero}>
            <div className={styles.heroBody}>
              <nav className={styles.crumbs} aria-label="Breadcrumb">
                <Link to="/services" className={styles.crumbLink}>
                  Services
                </Link>
                <span className={styles.crumbSep}>/</span>
                <span>{content.label}</span>
              </nav>
              <div className={styles.titleRow}>
                {content.badge && <img src={content.badge} alt="" className={styles.badge} />}
                <h1 className={styles.title}>
                  Welcome to {brand.shortName} {content.headline}
                </h1>
              </div>
              <p className={styles.lede}>{content.lede}</p>
              <div className={styles.coreLabel}>Core features</div>
              <div className={styles.features}>
                {content.coreFeatures.map((f) => (
                  <div key={f.title} className={styles.feature}>
                    <Check
                      size={15}
                      strokeWidth={2.6}
                      color="var(--vn-success-solid)"
                      className={styles.check}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div className={styles.featureTitle}>{f.title}</div>
                      <div className={styles.featureDesc}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Art block: only rendered when a hero image exists for this category. */}
            {hasArt && (
              <div className={styles.art} aria-hidden>
                <span className={styles.halo} />
                {hasDarkArt ? (
                  <>
                    <img
                      src={content.heroArt}
                      alt=""
                      className={`${styles.artImg} ${styles.artLight}`}
                      loading="lazy"
                    />
                    <img
                      src={content.heroArtDark}
                      alt=""
                      className={`${styles.artImg} ${styles.artDark}`}
                      loading="lazy"
                    />
                  </>
                ) : (
                  <img src={content.heroArt} alt="" className={styles.artImg} loading="lazy" />
                )}
              </div>
            )}
          </div>

          <div className={styles.statStrip}>
            <div className={styles.stat}>
              <span className={styles.statDot} style={{ background: 'var(--vn-success-solid)' }} />
              <span className={styles.statLabel}>Registered</span>
              <span className={styles.statValue}>{registered}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statDot} style={{ background: 'var(--vn-primary-solid)' }} />
              <span className={styles.statLabel}>Available products</span>
              <span className={styles.statValue}>{available}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statDot} style={{ background: 'var(--vn-warning-solid)' }} />
              <span className={styles.statLabel}>Coming soon</span>
              <span className={styles.statValue}>{comingSoon}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {category && (
            <Section title={category.title} description={category.description} style={{ marginBottom: 0 }}>
              <div className={grid.grid}>
                {isSecurity ? (
                  <>
                    {waapServices.map((s) => (
                      <ProvisionedCard key={`waap-${s.id}`} service={s} asWaap />
                    ))}
                    {wafServices.map((s) => (
                      <ProvisionedCard key={`waf-${s.id}`} service={s} asWaf />
                    ))}
                  </>
                ) : (
                  categoryServices.map((s) => (
                    <ProvisionedCard
                      key={`${categoryKey}-${s.id}`}
                      service={s}
                      providerCount={isCdn ? cdnGroupByCanonicalId.get(s.id) : undefined}
                    />
                  ))
                )}
                {catalog.map((p) => (
                  <ProductCard key={p.scope} product={p} category={category} />
                ))}
              </div>
            </Section>
          )}
        </div>
      </Container>
    </TooltipProvider>
  );
}
